/**
 * Étape 1 du parcours d'ajout : choix du type de widget (bâtiment, graphique, météo…).
 * Route : /widgets
 */
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router-dom";
import { Header } from "../../Header";
import { sectionToIcon } from "../../type/default";
import { AddWidgetLayout } from "./AddWidgetLayout";
import { getWidgetCount } from "./widgetCounts";
import { WIDGET_CATALOG, WidgetCatalogEntry } from "./widgetCatalog";
import { saveSizeSelection } from "./widgetDraft";
import { WidgetThumbnail } from "./WidgetThumbnail";

export function AddWidgetCatalogPage() {
  const navigate = useNavigate();

  // Plus d'étape de choix de taille : on applique une taille par défaut (medium
  // si dispo, sinon la première) et on va directement au configurateur.
  const chooseWidget = (entry: WidgetCatalogEntry) => {
    // Garde-fou : on n'entre pas dans le parcours si la limite est atteinte.
    if (entry.maxInstances !== undefined && getWidgetCount(entry.id) >= entry.maxInstances) {
      return;
    }
    const defaultSize = entry.sizes[1] ?? entry.sizes[0];
    saveSizeSelection(entry.id, defaultSize);
    navigate(`/widgets/${entry.id}/configure`);
  };

  return (
    <AddWidgetLayout>
      <Header title="Ajouter un widget" icon={sectionToIcon.section_dashboard} />
      <p className="text-sm text-grey-medium mt-4 mb-8 max-w-3xl">
        Choisissez le type de widget à ajouter à votre dashboard. Vous pourrez
        ensuite le paramétrer et le redimensionner.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {WIDGET_CATALOG.map((entry) => {
          // Vignette à la plus petite taille (rendu plus lisible une fois réduit).
          const thumbSize = entry.sizes[entry.sizes.length - 1];
          const thumbProps: Record<string, unknown> = {
            ...entry.defaultProps,
            ...(thumbSize.layoutVariant
              ? { layoutVariant: thumbSize.layoutVariant }
              : {}),
          };
          // Vignette météo : pas de localisation → données mock (aucun appel réseau).
          if (entry.id === "WeatherWidget") {
            delete thumbProps.latitude;
            delete thumbProps.longitude;
          }

          // Limite d'instances (ex. météo = 3 appels API max).
          const count = getWidgetCount(entry.id);
          const limitReached =
            entry.maxInstances !== undefined && count >= entry.maxInstances;

          return (
            <button
              key={entry.id}
              type="button"
              onClick={() => chooseWidget(entry)}
              disabled={limitReached}
              aria-disabled={limitReached}
              title={
                limitReached
                  ? `Limite atteinte (${entry.maxInstances} maximum)`
                  : undefined
              }
              className={`group h-full text-left rounded-2xl border bg-white overflow-hidden flex flex-col transition-all focus:outline-none focus:ring-2 focus:ring-secondary/40 ${
                limitReached
                  ? "border-grey-light opacity-60 cursor-not-allowed"
                  : "border-grey-light hover:border-secondary/50 hover:shadow-lg"
              }`}
            >
              <div className="h-44 bg-grey-light/30 border-b border-grey-light p-4">
                <WidgetThumbnail
                  widgetType={entry.id}
                  props={thumbProps}
                  w={thumbSize.w}
                  h={thumbSize.h}
                />
              </div>

              <div className="p-4 flex-1 flex flex-col gap-1">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-oxaniumMedium text-default-text text-base">
                    {entry.label}
                  </h3>
                  {limitReached ? (
                    <span className="text-xs font-oxaniumMedium text-grey-medium bg-grey-light rounded-full px-2 py-1 shrink-0">
                      {count}/{entry.maxInstances}
                    </span>
                  ) : (
                    <span className="flex items-center justify-center w-7 h-7 rounded-full bg-secondary/10 text-secondary group-hover:bg-secondary group-hover:text-white transition-colors shrink-0">
                      <FontAwesomeIcon icon="plus" className="text-xs" />
                    </span>
                  )}
                </div>
                <p className="text-sm text-grey-medium leading-snug">
                  {entry.description}
                </p>
                {limitReached && (
                  <p className="text-xs text-warning mt-1">
                    Limite atteinte ({entry.maxInstances} maximum).
                  </p>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </AddWidgetLayout>
  );
}
